import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import type { Client, ClientIdentity, IntakeSummary } from '@/domain/schema/v1';
import { useAutosave } from '../../hooks/useAutosave';
import { useLoad } from '../../hooks/useLoad';
import { useService } from '../../hooks/useService';
import { ConflictBanner, LinesInput, SaveIndicator, Select, TextArea, TextInput } from '../../components/fields';
import type { ClientOutlet } from './ClientShell';

/** 내담자 기본정보·초기 상담 요약·민감정보 (CLINICAL_DATA_MODEL §2·§3, DATA_SCHEMA §4) */
export function ClientProfile() {
  const { client, reloadClient } = useOutletContext<ClientOutlet>();
  const svc = useService();
  const { data } = useLoad(async () => ({ intake: await svc.ensureIntake(client.id) }), [svc, client.id]);
  return (
    <div className="split-2">
      <div className="stack">
        <ClientForm client={client} />
      </div>
      <div className="stack">
        {data && <IntakeForm intake={data.intake} />}
        <IdentityPanel clientId={client.id} />
        <CodeEditor client={client} onRenamed={reloadClient} />
      </div>
    </div>
  );
}

function ClientForm({ client }: { client: Client }) {
  const svc = useService();
  const a = useAutosave<Client>(client, (patch, rev) => svc.updateClient(client.id, patch, rev));
  const v = a.value!;
  return (
    <div className="card">
      <div className="card-head">
        <h2>기본 정보</h2>
        <SaveIndicator status={a.status} error={a.error} onRetry={() => void a.flush()} />
      </div>
      {a.status === 'conflict' && (
        <ConflictBanner
          onMine={() => void svc.getClient(client.id).then(a.resolveWithMine)}
          onTheirs={() => void svc.getClient(client.id).then(a.resolveWithTheirs)}
        />
      )}
      <TextInput label="별칭" value={v.alias} onChange={(x) => a.set('alias', x || undefined)} />
      <BirthYearInput value={v.birthYear} onCommit={(y) => a.set('birthYear', y)} />
      <Select
        label="성별"
        value={v.gender}
        options={[
          { value: '여', label: '여' },
          { value: '남', label: '남' },
          { value: '기재 안 함', label: '기재 안 함' },
          ...(v.gender && !['여', '남', '기재 안 함'].includes(v.gender) ? [{ value: v.gender, label: v.gender }] : []),
        ]}
        onChange={(x) => a.set('gender', x)}
      />
      <TextInput label="직업/역할" value={v.occupationRole} onChange={(x) => a.set('occupationRole', x || undefined)} />
      <TextInput label="학교(학점)" value={v.education} onChange={(x) => a.set('education', x || undefined)} />
      <TextInput label="종교" value={v.religion} onChange={(x) => a.set('religion', x || undefined)} />
      <TextInput
        label="알코올 등 물질 관련"
        value={v.substanceUse}
        onChange={(x) => a.set('substanceUse', x || undefined)}
      />
      <TextInput
        label="최대 상담 가능 회기 수"
        type="number"
        value={v.maxSessions ? String(v.maxSessions) : ''}
        onChange={(x) => a.set('maxSessions', Number(x) >= 1 ? Math.min(500, Math.round(Number(x))) : undefined)}
      />
      <TextInput label="의뢰 경로" value={v.referralSource} onChange={(x) => a.set('referralSource', x || undefined)} />
      <TextArea
        label="이전 상담 경험"
        value={v.priorCounselingHistory}
        onChange={(x) => a.set('priorCounselingHistory', x || undefined)}
      />
      <TextArea
        label="의료/정신과 이력"
        value={v.medicalPsychiatricHistory}
        onChange={(x) => a.set('medicalPsychiatricHistory', x || undefined)}
      />
      <LinesInput
        label="복용 약물"
        value={v.currentMedication}
        onChange={(x) => a.set('currentMedication', x.length ? x : undefined)}
      />
      <TextArea
        label="가족 구조"
        value={v.familyStructure}
        onChange={(x) => a.set('familyStructure', x || undefined)}
        hint="실명 대신 관계 역할로 (부, 모, 형 등)"
      />
      <LinesInput
        label="중요한 관계"
        value={v.importantRelationships}
        onChange={(x) => a.set('importantRelationships', x.length ? x : undefined)}
      />
      <TextArea
        label="신앙/영성 맥락"
        value={v.faithSpiritualContext}
        onChange={(x) => a.set('faithSpiritualContext', x || undefined)}
      />
      <LinesInput
        label="주요 생애 사건"
        value={v.majorLifeEvents}
        onChange={(x) => a.set('majorLifeEvents', x.length ? x : undefined)}
      />
      <LinesInput label="태그" value={v.tags} onChange={(x) => a.set('tags', x.length ? x : undefined)} />
      <Select
        label="상담 상태"
        value={v.status}
        options={[
          { value: 'active', label: '진행 중' },
          { value: 'paused', label: '보류' },
          { value: 'terminated', label: '종결' },
        ]}
        onChange={(x) => a.set('status', x)}
      />
    </div>
  );
}

function IntakeForm({ intake }: { intake: IntakeSummary }) {
  const svc = useService();
  const a = useAutosave<IntakeSummary>(intake, (patch, rev) => svc.updateIntake(intake.id, patch, rev));
  const v = a.value!;
  return (
    <div className="card">
      <div className="card-head">
        <h2>초기 상담 요약</h2>
        <SaveIndicator status={a.status} error={a.error} />
      </div>
      <LinesInput label="주호소" value={v.presentingProblems} onChange={(x) => a.set('presentingProblems', x)} />
      <TextInput label="시작 시기" value={v.onset} onChange={(x) => a.set('onset', x || undefined)} />
      <TextInput label="지속 기간" value={v.duration} onChange={(x) => a.set('duration', x || undefined)} />
      <TextArea
        label="기능 손상"
        value={v.functionalImpact}
        onChange={(x) => a.set('functionalImpact', x || undefined)}
      />
      <TextArea
        label="상담 신청/의뢰 사유"
        value={v.referralReason}
        onChange={(x) => a.set('referralReason', x || undefined)}
      />
      <TextArea
        label="내담자의 기대"
        value={v.clientExpectation}
        onChange={(x) => a.set('clientExpectation', x || undefined)}
      />
      <LinesInput label="강점" value={v.strengths} onChange={(x) => a.set('strengths', x.length ? x : undefined)} />
      <LinesInput
        label="지지 자원"
        value={v.supportResources}
        onChange={(x) => a.set('supportResources', x.length ? x : undefined)}
      />
      <LinesInput
        label="대처 자원"
        value={v.copingResources}
        onChange={(x) => a.set('copingResources', x.length ? x : undefined)}
      />
    </div>
  );
}

/** 민감정보: 기본 접힘, AI Packet에 포함되지 않음 */
function IdentityPanel({ clientId }: { clientId: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="card">
      <h2>민감정보 (실명·연락처)</h2>
      <p className="muted small">
        목록과 AI 분석 자료에는 포함되지 않습니다. 문서 인쇄 시 선택적으로 이름을 넣을 수 있습니다.
      </p>
      {open ? (
        <IdentityForm clientId={clientId} />
      ) : (
        <button type="button" onClick={() => setOpen(true)}>
          열기
        </button>
      )}
    </div>
  );
}

function IdentityForm({ clientId }: { clientId: string }) {
  const svc = useService();
  const { data } = useLoad(() => svc.ensureIdentity(clientId), [svc, clientId]);
  if (!data) return <p className="muted">불러오는 중…</p>;
  return <IdentityFields identity={data} />;
}

function IdentityFields({ identity }: { identity: ClientIdentity }) {
  const svc = useService();
  const a = useAutosave<ClientIdentity>(identity, (patch, rev) => svc.updateIdentity(identity.id, patch, rev));
  const v = a.value!;
  return (
    <>
      <SaveIndicator status={a.status} error={a.error} />
      <TextInput label="실명" value={v.realName} onChange={(x) => a.set('realName', x || undefined)} />
      <TextInput label="연락처" value={v.phone} onChange={(x) => a.set('phone', x || undefined)} />
      <TextInput
        label="생년월일"
        type="date"
        value={v.birthDate}
        onChange={(x) => a.set('birthDate', x || undefined)}
      />
      <TextInput label="주소" value={v.address} onChange={(x) => a.set('address', x || undefined)} />
      <TextInput
        label="비상연락처"
        value={v.emergencyContact}
        onChange={(x) => a.set('emergencyContact', x || undefined)}
      />
    </>
  );
}

/** 입력 중에는 칸에 그대로 보여 주고, 올바른 4자리 연도(또는 빈 칸)일 때만 저장한다 */
function BirthYearInput({ value, onCommit }: { value: number | undefined; onCommit: (y: number | undefined) => void }) {
  const [text, setText] = useState(value ? String(value) : '');
  const y = Number(text);
  const valid = text === '' || (/^\d{4}$/.test(text) && y >= 1900 && y <= new Date().getFullYear());
  return (
    <TextInput
      label="출생연도"
      value={text}
      hint={
        valid
          ? '예: 1991. 문서에는 기록 날짜 기준 나이로 자동 표기됩니다 (민감정보에 생년월일이 있으면 정확한 만 나이).'
          : '1900년~올해 사이의 4자리 연도를 입력하세요.'
      }
      onChange={(x) => {
        const t = x.replace(/[^0-9]/g, '').slice(0, 4);
        setText(t);
        const n = Number(t);
        if (t === '') onCommit(undefined);
        else if (t.length === 4 && n >= 1900 && n <= new Date().getFullYear()) onCommit(n);
      }}
    />
  );
}

/** 내담자 코드 (이니셜 권장: 김상담 → KSD). AI 자료·문서·Claude 프로젝트 이름에 쓰인다 */
function CodeEditor({ client, onRenamed }: { client: Client; onRenamed: () => void }) {
  const svc = useService();
  const [code, setCode] = useState(client.clientCode);
  const [err, setErr] = useState<string | null>(null);
  const changed = code.trim() !== client.clientCode;
  return (
    <div className="card stack">
      <h2>내담자 코드</h2>
      <div className="row">
        <input
          aria-label="내담자 코드"
          value={code}
          maxLength={32}
          onChange={(e) => (setCode(e.target.value), setErr(null))}
        />
        <button
          type="button"
          disabled={!changed}
          onClick={() =>
            void svc
              .renameClientCode(client.id, code)
              .then(onRenamed)
              .catch((e: Error) => setErr(e.message))
          }
        >
          코드 바꾸기
        </button>
      </div>
      {err && (
        <p role="alert" className="warn small">
          {err}
        </p>
      )}
      <p className="muted small">
        이니셜을 권합니다 (예: 김상담 → KSD, 겹치면 KSD2). AI 자료와 외부 AI 프로젝트 이름(상담｜코드)에 쓰입니다. 이미
        만든 문서에 적힌 옛 코드는 그대로 남고, 새로 만드는 문서부터 바뀝니다. 코드를 바꿨다면 외부 AI 프로젝트 이름도
        같이 바꿔 주세요.
      </p>
    </div>
  );
}
